interface FormatoPickerProps {
    onSelect: (formato: number) => void;
    selectedFormato: number;
    /** Ex.: perfil público — `[10]` para não mostrar Hentai. */
    excludeFormatIds?: number[];
    /** Alinhamento horizontal da linha no desktop (`md+`). */
    desktopAlign?: 'center' | 'end';
    /** Mostra o botão "Todos" como primeira opção (busca geral sem yaoi/hentai/yuri). */
    showTodos?: boolean;
}

/** `formt_id` na BD — usado p.ex. para esconder Hentai só no perfil público. */
export const FORMATO_ID_HENTAI = 10;

/** IDs enviados à API quando o usuário escolhe "Todos" (exclui yaoi, hentai, yuri). */
export const TODOS_FORMATO_IDS = [1, 3, 4, 5, 13] as const;

/** Sentinel para o estado "Todos" no picker (nunca enviado à API). */
export const FORMATO_TODOS_SENTINEL = 0;

/** Query `formato=` na URL para «Todos» na busca (ids ordenados). */
export const FORMATO_BUSCA_TODOS_QUERY = [...TODOS_FORMATO_IDS].sort((a, b) => a - b).join(',');

const FORMATOS = [
    { id: 'comic', label: 'Comic', formato: 5, color: '#22c55e' }, // Verde
    { id: 'shoujo', label: 'Shoujo', formato: 4, color: '#a855f7' }, // Roxo
    { id: 'yaoi', label: 'Yaoi', formato: 8, color: '#ec4899' }, // Rosa
    { id: 'yuri', label: 'Yuri', formato: 9, color: '#3b82f6' }, // Azul
    { id: 'hentai', label: 'Hentai', formato: FORMATO_ID_HENTAI, color: '#ef4444' }, // Vermelho
    { id: 'manga', label: 'Manga', formato: 13, color: '#f59e0b' }, // Laranja/Ambar
    { id: 'novel', label: 'Novel', formato: 3, color: '#0ea5e9' }, // Azul Claro
];

const TODOS_ITEM = { id: 'todos', label: 'Todos', formato: FORMATO_TODOS_SENTINEL, color: '#f97316' };

export default function FormatoPicker({
    onSelect,
    selectedFormato,
    excludeFormatIds = [],
    desktopAlign = 'center',
    showTodos = false,
}: FormatoPickerProps) {
    const excluded = new Set(excludeFormatIds);
    const formatos = FORMATOS.filter((f) => !excluded.has(f.formato));
    const items = showTodos ? [TODOS_ITEM, ...formatos] : formatos;

    return (
        <>
        {/* Mobile: grid 4 colunas */}
        <div className="md:hidden w-full py-2 px-2">
            <div className="grid grid-cols-4 gap-1 bg-white/[0.03] backdrop-blur-xl p-1.5 rounded-2xl border border-white/5 shadow-2xl">
                {items.map((formato) => {
                    const isSelected = formato.formato === selectedFormato;
                    return (
                        <button
                            key={formato.id}
                            onClick={() => onSelect(formato.formato)}
                            className={`
                                relative px-1 py-2.5 rounded-[14px] text-[9px] font-black transition-all duration-500
                                flex items-center justify-center w-full
                                ${isSelected
                                    ? 'text-white shadow-lg'
                                    : 'text-white/30 hover:text-white/60 hover:bg-white/5'
                                }
                            `}
                            style={isSelected ? {
                                backgroundColor: formato.color,
                                boxShadow: `0 4px 12px -4px ${formato.color}80`,
                            } : {}}
                        >
                            <span className="relative z-10 tracking-[0.06em] uppercase">
                                {formato.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>

        {/* Desktop: linha única horizontal */}
        <div
            className={`hidden md:flex w-full py-2 overflow-x-auto no-scrollbar ${
                desktopAlign === 'end' ? 'justify-end' : 'justify-center'
            }`}
        >
            <div className="inline-flex items-center gap-1 bg-white/[0.03] backdrop-blur-xl p-1 rounded-2xl border border-white/5 shadow-2xl min-w-max">
                {items.map((formato) => {
                    const isSelected = formato.formato === selectedFormato;
                    return (
                        <button
                            key={formato.id}
                            onClick={() => onSelect(formato.formato)}
                            className={`
                                relative px-4 py-2 rounded-[14px] text-[11px] font-black transition-all duration-500
                                flex items-center justify-center min-w-[80px] group whitespace-nowrap
                                ${isSelected
                                    ? 'text-white shadow-lg'
                                    : 'text-white/30 hover:text-white/60 hover:bg-white/5'
                                }
                            `}
                            style={isSelected ? {
                                backgroundColor: formato.color,
                                boxShadow: `0 4px 12px -4px ${formato.color}80`,
                            } : {}}
                        >
                            <span className="relative z-10 tracking-[0.1em] uppercase">
                                {formato.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
        </>
    );
}
